import { createContext, useContext, useMemo } from 'react';

import type {
  EventData,
  EventName,
  TrackJourneyEventOptions,
  UseObservabilityTrackJourneyEvent,
} from '@opendoor/observability';
import { TamaguiProvider } from '@tamagui/core';
import type { TamaguiProviderProps } from '@tamagui/core';
import { PortalProvider } from '@tamagui/portal';
import { ToastProvider, ToastViewport } from '@tamagui/toast';
import type { ToastViewportProps } from '@tamagui/toast';

import config from './tamagui.config';

type PlatformEventName = EventName<'platform'>;

/**
 * Bricks components only emit platform category events; keep this narrowed to
 * avoid propagating an unnecessary all-category generic surface to consumers.
 */
type TrackJourneyEvent = <E extends PlatformEventName>(
  categoryName: 'platform',
  eventName: E,
  eventData: EventData<'platform', E>,
  options?: TrackJourneyEventOptions,
) => void;

type HookTrackJourneyEvent = UseObservabilityTrackJourneyEvent;
const noopTrackJourneyEvent: HookTrackJourneyEvent = () => {};

export type BricksObservability = {
  trackJourneyEvent: TrackJourneyEvent;
};

export type BricksUseObservability = () => {
  trackJourneyEvent: HookTrackJourneyEvent;
};

export interface IAnalyticsContext {
  useObservability: BricksUseObservability;
}

export const defaultAnalyticsContext: IAnalyticsContext = {
  useObservability: () => {
    return {
      trackJourneyEvent: noopTrackJourneyEvent,
    };
  },
};

export const AnalyticsContext = createContext<IAnalyticsContext>(defaultAnalyticsContext);

/**
 * Hook for using the AnalyticsContext context
 */
export const useAnalyticsContext = () => useContext(AnalyticsContext);

// TODO: Decide if/how we want to allow bricks consumers to override config.
export type BricksProviderProps = Omit<TamaguiProviderProps, 'config'> & {
  useObservability: IAnalyticsContext['useObservability'];
  toastViewportProps?: ToastViewportProps;
};

export function BricksProvider({
  children,
  useObservability,
  toastViewportProps,
  ...rest
}: BricksProviderProps) {
  useObservability = useObservability ?? defaultAnalyticsContext.useObservability;

  const context = useMemo(
    () => ({
      useObservability,
    }),
    [useObservability],
  );

  return (
    <AnalyticsContext.Provider value={context}>
      <TamaguiProvider config={config} defaultTheme="light" {...rest}>
        <PortalProvider>
          <ToastProvider>
            {children}
            <ToastViewport
              flexDirection="column"
              top={0}
              left={0}
              right={0}
              {...toastViewportProps}
            />
          </ToastProvider>
        </PortalProvider>
      </TamaguiProvider>
    </AnalyticsContext.Provider>
  );
}
